import { ModalHeader, ModalBody, ModalFooter } from '@/components/ui/modal';
import { useTransactionFormState } from '@/hooks/useTransactionFormState';
import { useTransactionFormValidation } from '@/hooks/useTransactionFormValidation';
import { useTransactionFormHandlers } from '@/hooks/useTransactionFormHandlers';
import { useTransactionFormEffects } from '@/hooks/useTransactionFormEffects';
import { TransactionFormFieldsNew } from './TransactionFormFieldsNew';
import { DollarSign } from 'lucide-react';
import { useFormSubmission } from '@/hooks/useFormSubmission';
import { useCategories } from '@/contexts/categories';
import { useMemo } from 'react';

interface TransactionFormNewProps {
  onClose: () => void;
  onSubmit: (data: any) => Promise<void> | void;
  onSuccess?: () => void;
  initialData?: any;
  cards?: Array<{
    id: string;
    name: string;
    bank: string;
  }>;
  isLoading?: boolean;
}

export const TransactionFormNew = ({
  onClose,
  onSubmit,
  onSuccess,
  initialData,
  cards = [],
  isLoading = false
}: TransactionFormNewProps) => {
  const { isSubmitting, handleSubmit } = useFormSubmission();
  const { categories } = useCategories();
  
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    amountString,
    setAmountString
  } = useTransactionFormState({ initialData });

  const {
    debouncedValidateAmount,
    clearFieldError
  } = useTransactionFormValidation(setErrors);

  const {
    handleSubmit: handleFormSubmit,
    handleInputChange,
    handleAmountChange,
    handleTypeChange
  } = useTransactionFormHandlers({
    formData,
    setFormData,
    errors,
    setErrors,
    clearFieldError,
    debouncedValidateAmount,
    setAmountString,
    onSubmit: async (data: any) => {
      console.log('=== FORM SUBMISSION ===');
      console.log('Data to submit:', data);
      
      // Basic validation - let the schema handle the detailed validation
      if (!data.amount || data.amount <= 0) {
        throw new Error('Valor deve ser maior que zero');
      }
      
      if (!data.categoryId?.trim()) {
        throw new Error('Categoria é obrigatória');
      }
      
      if (!data.date?.trim()) {
        throw new Error('Data é obrigatória');
      }
      
      if (!data.type) {
        throw new Error('Tipo da transação é obrigatório');
      }
      
      // Payment method validation
      if (data.paymentMethod === 'cartao' && data.type === 'despesa' && !data.cardId) {
        throw new Error('Cartão é obrigatório quando meio de pagamento é cartão');
      }
      
      if (data.paymentMethod !== 'cartao' && !data.accountId) {
        throw new Error('Conta é obrigatória');
      }
      
      console.log('Validation passed, calling onSubmit');
      await onSubmit(data);
    }
  });

  // Effects for form synchronization
  useTransactionFormEffects({
    formData,
    setFormData,
    categories,
    errors,
    clearFieldError,
    isEditing: !!initialData
  });

  // Available categories based on transaction type
  const availableCategories = useMemo(() => {
    return categories.filter(cat => cat.type === formData.type);
  }, [categories, formData.type]);

  const effectiveLoading = isLoading || isSubmitting;

  return (
    <div className="flex flex-col h-full">
      <ModalHeader
        title={!!initialData ? 'Editar Transação' : 'Nova Transação'}
        description={!!initialData ? 'Edite os dados da transação financeira' : 'Adicione uma nova transação financeira'}
        icon={DollarSign}
        onClose={onClose}
      />

      <ModalBody>
        <form 
          id="transaction-form-new" 
          onSubmit={handleFormSubmit}
        >
          <TransactionFormFieldsNew
            formData={formData} 
            amountString={amountString} 
            errors={errors} 
            isLoading={effectiveLoading} 
            availableCategories={availableCategories} 
            cards={cards} 
            onInputChange={handleInputChange} 
            onAmountChange={handleAmountChange} 
            onTypeChange={handleTypeChange}
            isEditing={!!initialData}
          />
        </form>
      </ModalBody>

      <ModalFooter
        primaryAction={{
          label: effectiveLoading ? 'Salvando...' : (!!initialData ? 'Atualizar Transação' : 'Salvar Transação'),
          type: 'submit',
          formId: 'transaction-form-new',
          isLoading: effectiveLoading
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: onClose
        }}
      />
    </div>
  );
};